import { useState, useRef, useCallback, useMemo } from "react";
import { Mic, Square, Loader2, HelpCircle, X, Check, Pencil, AlertTriangle } from "lucide-react";
import { PostLoadCard, PostLoadCardHeader } from "@/components/post-load/PostLoadCard";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { getLocalTodayYMD, formatYYYYMMDDToLocaleDateString } from "@/lib/dateOnly";
import { EQUIPMENT_FLAT_LIST } from "@/lib/equipmentTypes";
import { useMicVolumeMeter } from "@/hooks/useMicVolumeMeter";
import MicVolumeMeter from "@/components/ai-post/MicVolumeMeter";

export interface VoicePostTruckFields {
  origin_city?: string | null;
  origin_state?: string | null;
  destination_city?: string | null;
  destination_state?: string | null;
  open_to_anywhere?: boolean;
  equipment_type?: string | null;
  available_date?: string | null; // YYYY-MM-DD
  length_ft?: number | null;
  max_weight_lbs?: number | null;
  rate_per_mile?: number | null;
  notes?: string | null;
}

type VoiceState = "idle" | "recording" | "processing" | "done" | "error";

interface VoicePostTruckCardProps {
  onPostTruck?: (fields: VoicePostTruckFields) => void;
  isPosting?: boolean;
}

type EditableFieldKey = "origin" | "destination" | "equipment" | "date" | "length" | "weight";

const GUIDE_FIELDS: {
  key: EditableFieldKey;
  label: string;
  required: boolean;
  check: (f: VoicePostTruckFields) => boolean;
  value: (f: VoicePostTruckFields) => string | null;
}[] = [
  { key: "origin", label: "Origin", required: true, check: (f) => !!f.origin_city, value: (f) => f.origin_city && f.origin_state ? `${f.origin_city}, ${f.origin_state}` : f.origin_city || null },
  { key: "destination", label: "Destination", required: false, check: (f) => !!f.destination_city || !!f.open_to_anywhere, value: (f) => f.open_to_anywhere ? "Anywhere" : (f.destination_city && f.destination_state ? `${f.destination_city}, ${f.destination_state}` : f.destination_city || null) },
  { key: "equipment", label: "Equipment", required: true, check: (f) => !!f.equipment_type, value: (f) => f.equipment_type || null },
  { key: "date", label: "Available", required: true, check: (f) => !!f.available_date, value: (f) => f.available_date ? formatYYYYMMDDToLocaleDateString(f.available_date, undefined, { month: "short", day: "numeric" }) : null },
  { key: "length", label: "Length", required: false, check: (f) => !!f.length_ft, value: (f) => f.length_ft ? `${f.length_ft} ft` : null },
  { key: "weight", label: "Max Wt", required: false, check: (f) => !!f.max_weight_lbs, value: (f) => f.max_weight_lbs ? `${(f.max_weight_lbs / 1000).toFixed(0)}k lbs` : null },
];

const COMMON_EQUIPMENT = ["Reefer", "Dry Van", "Flatbed", "Step Deck", "Van", "Power Only", "Conestoga", "Lowboy", "Hotshot"];

function getSanityWarnings(f: VoicePostTruckFields): string[] {
  const warnings: string[] = [];
  if (f.max_weight_lbs && f.max_weight_lbs > 80000) warnings.push("Max weight exceeds typical truck limits");
  if (f.length_ft && f.length_ft > 75) warnings.push("Length seems unusually long — please double-check");
  if (f.length_ft && f.length_ft < 10) warnings.push("Length seems unusually short — please double-check");
  return warnings;
}

/** Map a load-shaped voice response into a truck-shaped field set. */
function adaptLoadFieldsToTruck(loadFields: any, transcript: string): Partial<VoicePostTruckFields> {
  const out: Partial<VoicePostTruckFields> = {};
  if (!loadFields) return out;

  if (loadFields.origin_city) out.origin_city = loadFields.origin_city;
  if (loadFields.origin_state) out.origin_state = loadFields.origin_state;
  if (loadFields.destination_city) out.destination_city = loadFields.destination_city;
  if (loadFields.destination_state) out.destination_state = loadFields.destination_state;
  if (loadFields.equipment_type) out.equipment_type = loadFields.equipment_type;
  if (loadFields.pickup_date) out.available_date = loadFields.pickup_date;
  if (loadFields.weight_lbs) out.max_weight_lbs = loadFields.weight_lbs;

  // Detect "anywhere" intent for destination
  const anywhereRe = /\b(any\s*where|open\s*to\s*anywhere|wherever|anywhere)\b/i;
  if (anywhereRe.test(transcript || "")) {
    out.open_to_anywhere = true;
    out.destination_city = null;
    out.destination_state = null;
  }

  return out;
}

export default function VoicePostTruckCard({ onPostTruck, isPosting }: VoicePostTruckCardProps) {
  const { toast } = useToast();
  const [state, setState] = useState<VoiceState>("idle");
  const [transcript, setTranscript] = useState("");
  const [fields, setFields] = useState<VoicePostTruckFields>({ available_date: getLocalTodayYMD() });
  const [errorMsg, setErrorMsg] = useState("");
  const [showHints, setShowHints] = useState(false);
  const [editingField, setEditingField] = useState<EditableFieldKey | null>(null);
  const [editValue, setEditValue] = useState("");
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const cancelledRef = useRef(false);
  const [activeStream, setActiveStream] = useState<MediaStream | null>(null);
  const micLevel = useMicVolumeMeter(activeStream, state === "recording");

  const effectiveFields = useMemo(() => ({
    available_date: getLocalTodayYMD(),
    ...fields,
  }), [fields]);

  const sanityWarnings = useMemo(() => state === "done" ? getSanityWarnings(effectiveFields) : [], [state, effectiveFields]);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : "audio/mp4";

      const recorder = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      cancelledRef.current = false;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setActiveStream(null);
        if (cancelledRef.current) {
          cancelledRef.current = false;
          chunksRef.current = [];
          setState("idle");
          setTranscript("");
          setFields({ available_date: getLocalTodayYMD() });
          setErrorMsg("");
          setEditingField(null);
          return;
        }
        const blob = new Blob(chunksRef.current, { type: mimeType });
        await processAudio(blob);
      };

      recorder.start();
      mediaRecorderRef.current = recorder;
      setActiveStream(stream);
      setState("recording");
    } catch (err) {
      console.error("Mic access error:", err);
      toast({
        variant: "destructive",
        title: "Microphone access denied",
        description: "Please allow microphone access to use Voice Post Truck.",
      });
    }
  }, [toast]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === "recording") {
      cancelledRef.current = false;
      mediaRecorderRef.current.stop();
      setState("processing");
    }
  }, []);

  const cancelRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === "recording") {
      cancelledRef.current = true;
      mediaRecorderRef.current.stop();
    }
  }, []);

  const processAudio = async (blob: Blob) => {
    try {
      const formData = new FormData();
      formData.append("audio", blob, blob.type.includes("mp4") ? "recording.m4a" : "recording.webm");
      formData.append("mode", "truck");
      // Stale-date fix: send the speaker's real local today + timezone so the
      // parser resolves relative dates ("tomorrow", "Monday") correctly.
      formData.append("client_date", getLocalTodayYMD());
      formData.append("client_tz", Intl.DateTimeFormat().resolvedOptions().timeZone || "");

      const { data: { session } } = await supabase.auth.getSession();
      const headers: Record<string, string> = {
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      };
      if (session?.access_token) {
        headers.Authorization = `Bearer ${session.access_token}`;
      }

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/voice-post`,
        { method: "POST", headers, body: formData }
      );

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Unknown error" }));
        throw new Error(err.error || `Error ${res.status}`);
      }

      const data = await res.json();
      const tx = data.transcript || "";
      setTranscript(tx);
      // Truck mode returns truck-shaped fields directly. Keep anywhere safety net.
      const truckFields = { ...(data.fields || {}) } as Partial<VoicePostTruckFields>;
      const anywhereRe = /\b(any\s*where|open\s*to\s*anywhere|wherever)\b/i;
      if (anywhereRe.test(tx) || truckFields.open_to_anywhere) {
        truckFields.open_to_anywhere = true;
        truckFields.destination_city = null;
        truckFields.destination_state = null;
      }
      setFields((prev) => ({ ...prev, ...truckFields }));
      setState("done");
    } catch (err) {
      console.error("Voice post truck error:", err);
      const msg = err instanceof Error ? err.message : "Processing failed";
      setErrorMsg(msg);
      setState("error");
      toast({ variant: "destructive", title: "Voice Post Truck failed", description: msg });
    }
  };

  const reset = () => {
    setState("idle");
    setTranscript("");
    setFields({ available_date: getLocalTodayYMD() });
    setErrorMsg("");
    setEditingField(null);
  };

  const handlePostTruck = () => {
    onPostTruck?.(effectiveFields);
  };

  const startEditing = (key: EditableFieldKey) => {
    if (state !== "done") return;
    setEditingField(key);
    switch (key) {
      case "origin":
        setEditValue(effectiveFields.origin_city && effectiveFields.origin_state ? `${effectiveFields.origin_city}, ${effectiveFields.origin_state}` : effectiveFields.origin_city || "");
        break;
      case "destination":
        setEditValue(effectiveFields.open_to_anywhere ? "Anywhere" : (effectiveFields.destination_city && effectiveFields.destination_state ? `${effectiveFields.destination_city}, ${effectiveFields.destination_state}` : effectiveFields.destination_city || ""));
        break;
      case "equipment":
        setEditValue(effectiveFields.equipment_type || "");
        break;
      case "date":
        setEditValue(effectiveFields.available_date || "");
        break;
      case "length":
        setEditValue(effectiveFields.length_ft ? String(effectiveFields.length_ft) : "");
        break;
      case "weight":
        setEditValue(effectiveFields.max_weight_lbs ? String(effectiveFields.max_weight_lbs) : "");
        break;
    }
  };

  const saveEdit = () => {
    if (!editingField) return;
    const trimmed = editValue.trim();
    setFields((prev) => {
      const next = { ...prev };
      switch (editingField) {
        case "origin": {
          const parts = trimmed.split(",").map((s) => s.trim());
          next.origin_city = parts[0] || null;
          next.origin_state = parts[1] || prev.origin_state || null;
          break;
        }
        case "destination": {
          if (/^anywhere$/i.test(trimmed)) {
            next.open_to_anywhere = true;
            next.destination_city = null;
            next.destination_state = null;
          } else {
            next.open_to_anywhere = false;
            const parts = trimmed.split(",").map((s) => s.trim());
            next.destination_city = parts[0] || null;
            next.destination_state = parts[1] || prev.destination_state || null;
          }
          break;
        }
        case "equipment":
          next.equipment_type = trimmed || null;
          break;
        case "date":
          next.available_date = trimmed || null;
          break;
        case "length":
          next.length_ft = trimmed ? Number(trimmed.replace(/[^0-9]/g, "")) || null : null;
          break;
        case "weight":
          next.max_weight_lbs = trimmed ? Number(trimmed.replace(/[^0-9]/g, "")) || null : null;
          break;
      }
      return next;
    });
    setEditingField(null);
    setEditValue("");
  };

  const cancelEdit = () => {
    setEditingField(null);
    setEditValue("");
  };

  const requiredOk =
    !!effectiveFields.origin_city &&
    !!effectiveFields.equipment_type &&
    !!effectiveFields.available_date;

  return (
    <PostLoadCard className="flex flex-col">
      <PostLoadCardHeader
        icon={<Mic className="h-3.5 w-3.5" style={{ color: "hsl(170 50% 15%)" }} />}
        title="Voice Post Truck"
        demoHintKey="section.voicePostTruck"
        variant="atlas"
      >
        {state !== "idle" && (
          <button className="text-muted-foreground hover:text-foreground transition-colors" onClick={reset}>
            <X className="h-4 w-4" />
          </button>
        )}
        {state === "idle" && (
          <button className="text-muted-foreground hover:text-foreground transition-colors" onClick={() => setShowHints(true)}>
            <HelpCircle className="h-4 w-4" />
          </button>
        )}
      </PostLoadCardHeader>

      <div className="px-3 pb-3 pt-3 flex flex-col flex-1 space-y-3">
        <p className="text-[11px] text-muted-foreground leading-relaxed">
          {state === "idle" && "Say your truck info — origin, destination (or \"anywhere\"), equipment, available date. We'll fill what you mention."}
          {state === "recording" && "Listening... describe your truck, then tap to stop."}
          {state === "processing" && "Processing your audio with Atlas AI..."}
          {state === "done" && "Review below. Tap any chip to edit, then Post Truck."}
          {state === "error" && (errorMsg || "Something went wrong. Try again.")}
        </p>

        <div className="flex flex-col items-center gap-2 py-2">
          <div className="relative">
            {state === "recording" && (
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 rounded-full"
                style={{
                  border: "2px solid hsl(0 72% 51% / 0.5)",
                  animation: "ping 1.4s cubic-bezier(0,0,0.2,1) infinite",
                }}
              />
            )}
            <button
              onClick={state === "recording" ? stopRecording : state === "idle" || state === "error" ? startRecording : undefined}
              disabled={state === "processing"}
              className="relative h-14 w-14 rounded-full flex items-center justify-center transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background:
                  state === "recording"
                    ? "hsl(0 72% 51%)"
                    : "linear-gradient(135deg, hsl(170 85% 32%), hsl(170 75% 42%))",
                boxShadow:
                  state === "recording"
                    ? "0 0 18px 4px hsl(0 72% 51% / 0.35)"
                    : "0 0 18px 4px hsl(170 85% 32% / 0.28)",
              }}
            >
              {state === "processing" ? (
                <Loader2 className="h-6 w-6 text-white animate-spin" />
              ) : state === "recording" ? (
                <Square className="h-5 w-5 text-white fill-white" />
              ) : (
                <Mic className="h-6 w-6 text-white" />
              )}
            </button>
          </div>
          {state === "recording" && (
            <MicVolumeMeter level={micLevel} />
          )}
          <span className="text-[11px] text-muted-foreground italic">
            {state === "idle" && "Tap to start"}
            {state === "recording" && "Listening — tap to stop"}
            {state === "processing" && "Analyzing..."}
            {state === "done" && "Complete"}
            {state === "error" && "Tap to retry"}
          </span>
        </div>

        {(state === "done" || state === "processing") && (
          <div className="rounded-md border border-border bg-muted/40 px-2.5 py-2 text-[11px] text-foreground leading-relaxed min-h-[40px]">
            {state === "processing" ? (
              <span className="text-muted-foreground italic">Transcribing...</span>
            ) : (
              `"${transcript}"`
            )}
          </div>
        )}

        {state === "idle" && (
          <div className="rounded-md border border-border bg-muted/40 px-2.5 py-2 text-[11px] text-muted-foreground leading-relaxed italic">
            "Reefer out of Nogales AZ, heading anywhere, available Monday, 53 ft, max 44k lbs..."
          </div>
        )}

        {/* Guided field chips */}
        <div className="flex flex-wrap gap-1.5">
          {GUIDE_FIELDS.map((gf) => {
            const filled = gf.check(effectiveFields);
            const displayValue = filled ? gf.value(effectiveFields) : null;
            const isEditing = editingField === gf.key;
            return (
              <Popover key={gf.key} open={isEditing} onOpenChange={(open) => { if (!open) cancelEdit(); }}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    onClick={() => state === "done" && startEditing(gf.key)}
                    className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium transition-all duration-300 ${
                      state === "done" ? "cursor-pointer hover:ring-1 hover:ring-ring/50" : "cursor-default"
                    } ${
                      filled
                        ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30"
                        : gf.required
                          ? "text-muted-foreground border-border bg-transparent"
                          : "text-muted-foreground/70 border-dashed border-border/70 bg-transparent"
                    }`}
                  >
                    {filled && <Check className="h-3 w-3 mr-1 shrink-0" />}
                    {displayValue || gf.label}
                    {gf.required && !filled && <span className="ml-0.5 text-destructive">*</span>}
                    {state === "done" && <Pencil className="h-2.5 w-2.5 ml-1 opacity-40" />}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-2" side="bottom" align="start">
                  <div className="space-y-2">
                    <label className="text-[11px] font-medium text-foreground">{gf.label}</label>
                    {gf.key === "equipment" ? (
                      <select
                        className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                      >
                        <option value="">Select...</option>
                        {COMMON_EQUIPMENT.map((eq) => (
                          <option key={eq} value={eq}>{eq}</option>
                        ))}
                        {EQUIPMENT_FLAT_LIST.filter(e => !COMMON_EQUIPMENT.some(c => c.toLowerCase() === e.name.toLowerCase())).slice(0, 20).map((eq) => (
                          <option key={eq.enumValue} value={eq.name}>{eq.name}</option>
                        ))}
                      </select>
                    ) : gf.key === "date" ? (
                      <Input
                        type="date"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="h-8 text-xs"
                      />
                    ) : gf.key === "length" || gf.key === "weight" ? (
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          placeholder={gf.key === "length" ? "e.g. 53" : "e.g. 44000"}
                          className="h-8 text-xs"
                        />
                        <span className="text-xs text-muted-foreground">{gf.key === "length" ? "ft" : "lbs"}</span>
                      </div>
                    ) : (
                      <Input
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        placeholder={gf.key === "origin" ? "City, ST" : gf.key === "destination" ? "City, ST or Anywhere" : ""}
                        className="h-8 text-xs"
                      />
                    )}
                    <div className="flex gap-1.5 justify-end">
                      <button
                        onClick={cancelEdit}
                        className="rounded px-2 py-1 text-[10px] text-muted-foreground hover:bg-muted transition-colors"
                      >
                        <X className="h-3 w-3" />
                      </button>
                      <button
                        onClick={saveEdit}
                        className="rounded px-2 py-1 text-[10px] text-emerald-700 hover:bg-emerald-500/10 transition-colors"
                      >
                        <Check className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            );
          })}
        </div>

        {state === "done" && sanityWarnings.length > 0 && (
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-1.5 text-[10px] text-amber-700 dark:text-amber-400 space-y-0.5">
            {sanityWarnings.map((w) => (
              <div key={w} className="flex items-start gap-1">
                <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />
                <span>{w}</span>
              </div>
            ))}
          </div>
        )}

        <div className="mt-auto space-y-2 pt-1">
          {state === "recording" ? (
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={cancelRecording}
                className="w-full rounded-md py-2 text-[11px] font-medium border border-border text-muted-foreground hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={stopRecording}
                className="w-full rounded-md py-2 text-[11px] font-semibold text-white transition-colors"
                style={{ background: "linear-gradient(135deg, hsl(170 85% 32%), hsl(170 75% 42%))" }}
              >
                Stop & Process
              </button>
            </div>
          ) : state === "done" ? (
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={reset}
                disabled={isPosting}
                className="w-full rounded-md py-2 text-[11px] font-medium border border-border text-muted-foreground hover:bg-muted transition-colors disabled:opacity-50"
              >
                Start Over
              </button>
              <button
                type="button"
                disabled={!requiredOk || isPosting}
                onClick={handlePostTruck}
                className="w-full rounded-md py-2 text-[11px] font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: "hsl(170 85% 32% / 0.18)",
                  color: "hsl(var(--brand-teal-deep))",
                }}
              >
                {isPosting ? (
                  <span className="inline-flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Posting Truck…</span>
                ) : (
                  "Post Truck"
                )}
              </button>
            </div>
          ) : state === "error" ? (
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={reset}
                className="w-full rounded-md py-2 text-[11px] font-medium border border-border text-muted-foreground hover:bg-muted transition-colors"
              >
                Start Over
              </button>
              <button
                onClick={startRecording}
                className="w-full rounded-md py-2 text-[11px] font-semibold text-white transition-colors"
                style={{ background: "linear-gradient(135deg, hsl(170 85% 32%), hsl(170 75% 42%))" }}
              >
                Try Again
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {showHints && (
        <div
          className="absolute inset-0 z-20 bg-background/95 rounded-xl p-4 flex flex-col text-xs"
          onClick={() => setShowHints(false)}
        >
          <div className="font-semibold mb-2">Voice Post Truck — what to say</div>
          <ul className="list-disc pl-4 space-y-1 text-muted-foreground">
            <li>Origin city + state (required)</li>
            <li>Destination city + state, or just say "anywhere"</li>
            <li>Equipment type (required)</li>
            <li>Available date (required)</li>
            <li>Length, max weight (optional)</li>
          </ul>
          <span className="mt-auto text-[10px] text-muted-foreground italic">Tap anywhere to close</span>
        </div>
      )}
    </PostLoadCard>
  );
}
